'use client';

import { useEffect } from "react";
import styles from "./MediaModal.module.css";

/**
 * MediaModal component
 * 
 * Displays a modal containing either an image or video with navigation controls.
 * Handles keyboard events for accessibility (Escape, Left, Right).
 * 
 * Props:
 * - medias: array of media objects ({ image?, video?, title })
 * - currentIndex: index of the currently displayed media
 * - isOpen: boolean indicating if the modal is open
 * - onClose: function to close the modal
 * - onPrev: function to navigate to previous media
 * - onNext: function to navigate to next media
 */
export default function MediaModal({
  medias,
  currentIndex,
  isOpen,
  onClose,
  onPrev,
  onNext,
}) {

  // Listen for keyboard events when the modal is open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
          onClose(); // Close modal with Escape key
          break;
        case "ArrowLeft":
          onPrev(); // Go to previous media with Left Arrow
          break;
        case "ArrowRight":
          onNext(); // Go to next media with Right Arrow
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onPrev, onNext]);

  // If the modal is closed or no media is available, render nothing
  if (!isOpen || !medias || medias.length === 0) return null;

  // Retrieve the current media based on currentIndex
  const media = medias[currentIndex];

  return (
    // Modal overlay (clicking outside the modal closes it)
    <div className={styles.overlay} onClick={onClose}>
      {/* Stop click propagation to prevent closing when interacting with modal content */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Close button (X icon) */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 384 512"
          className={styles.close}
          onClick={onClose}
          fill="#901C1C"
        >
          <path d="M55.1 73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L147.2 256 9.9 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192.5 301.3 329.9 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.8 256 375.1 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192.5 210.7 55.1 73.4z"/>
        </svg>

        {/* Previous media button (left chevron) */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 320 512"
          className={`${styles.chevron} ${styles.left}`}
          onClick={onPrev}
          fill="#901C1C"
        >
          <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/>
        </svg>

        {/* Display image or video based on the media type */}
        {media.image ? (
          <img
            src={"/assets/" + media.image}
            alt={media.title}
            className={styles.media}
          />
        ) : (
          <video
            src={"/assets/" + media.video}
            controls
            className={styles.media}
          />
        )}

        {/* Media title displayed below */}
        <p className={styles.title}>{media.title}</p>

        {/* Next media button (right chevron) */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 320 512"
          className={`${styles.chevron} ${styles.right}`}
          onClick={onNext}
          fill="#901C1C"
        >
          <path d="M311.1 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/>
        </svg>
      </div>
    </div>
  );
}
